import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Boost } from 'atoms/icons/Boost';
import { Collection } from 'atoms/icons/Collection';
import { LineupsIcon } from 'atoms/icons/LineupsIcon';
import { BaseAccordion } from 'atoms/layout/Accordion';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyL, BodyM, BrandM, BrandXXL } from 'atoms/typography';
import { Img } from 'atoms/ui/Img';
import { useIsLaptop } from 'hooks/device/useIsLaptop';
import { laptopAndAbove } from 'style/mediaQuery';

import boost from './assets/boost.webp';
import card from './assets/card.webp';
import field from './assets/field.webp';
import lineup from './assets/lineup.webp';

const Root = styled.section`
  display: flex;
  padding: var(--quadruple-unit);
  align-items: flex-start;
  @media ${laptopAndAbove} {
    justify-content: space-between;
    padding: calc(var(--unit) * 15);
    height: 100vh;
  }
`;

const TextContainer = styled(Vertical).attrs({
  gap: 6,
})`
  justify-content: flex-start;
  @media ${laptopAndAbove} {
    width: 50%;
  }
  min-height: calc(var(--unit) * 93);
`;

const AccordionTitle = styled(BrandM)`
  color: var(--c-nd-400-opaque);
  &.isOpen {
    color: var(--white);
  }
`;

const IconWrapper = styled.div`
  width: var(--quadruple-unit);
`;

const ImgContainer = styled(Horizontal).attrs({
  center: true,
})`
  width: 50%;
  height: 100%;
  &.withBackgroundField {
    background-image: url(${field});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    mask-image: linear-gradient(
      to bottom,
      transparent 0%,
      black 10%,
      black 90%,
      transparent 100%
    );
  }
`;

type AccordionId = 'card-xp' | 'lineup-bonus' | 'collection-bonus' | undefined;

export const LevelUp = () => {
  const isLaptop = useIsLaptop();
  const [openAccordionId, setOpenAccordionId] =
    useState<AccordionId>('card-xp');

  const handleAccordionChange = (accordionId: AccordionId) => {
    if (openAccordionId === accordionId) {
      setOpenAccordionId(undefined);
    } else {
      setOpenAccordionId(accordionId);
    }
  };
  const accordionImages = {
    'card-xp': {
      image: card,
      background: field,
      height: '547px',
      width: '252px',
    },
    'lineup-bonus': {
      image: lineup,
      background: field,
      height: '564px',
      width: '516px',
    },
    'collection-bonus': {
      image: boost,
      background: null,
      height: '544px',
      width: '423px',
    },
  };

  return (
    <Root id="HowToPlay.navbar.level-up">
      <TextContainer>
        <Vertical gap={4}>
          <Vertical gap={2}>
            <BrandM uppercase color="var(--c-limited)">
              <FormattedMessage
                id="HowToPlay.ScoutAndCollect.gameplayStrategy"
                defaultMessage="Gameplay strategy"
              />
            </BrandM>
            <BrandXXL uppercase>
              <FormattedMessage
                id="HowToPlay.levelUp.title"
                defaultMessage="Level Up Your Game"
              />
            </BrandXXL>
            <BodyL>
              <FormattedMessage
                id="HowToPlay.levelUp.Description"
                defaultMessage="Boost your teams with a series of card and lineup specific
scoring bonuses, including:"
              />
            </BodyL>
            <BaseAccordion
              expanded={openAccordionId === 'card-xp'}
              onExpandChange={() => handleAccordionChange('card-xp')}
              title={
                <Horizontal>
                  <IconWrapper>
                    <Boost size={2} color="var(--c-blue-400)" />
                  </IconWrapper>
                  <AccordionTitle
                    className={classNames({
                      isOpen: openAccordionId === 'card-xp',
                    })}
                  >
                    <FormattedMessage
                      id="HowToPlay.levelUp.cardXp"
                      defaultMessage="Card XP"
                    />
                  </AccordionTitle>
                </Horizontal>
              }
            >
              <Vertical gap={2} center>
                <BodyM>
                  <FormattedMessage
                    id="HowToPlay.levelUp.cardXpDescription"
                    defaultMessage="As you play, your cards will collect XP and Level Up, granting you additional points when they play."
                  />
                </BodyM>
                {!isLaptop && (
                  <Img
                    src={accordionImages['card-xp'].image}
                    alt=""
                    width={295}
                    height={504}
                  />
                )}
              </Vertical>
            </BaseAccordion>
            <BaseAccordion
              expanded={openAccordionId === 'lineup-bonus'}
              onExpandChange={() => handleAccordionChange('lineup-bonus')}
              title={
                <Horizontal>
                  <IconWrapper>
                    <LineupsIcon size={3} color="var(--c-blue-400)" />
                  </IconWrapper>
                  <AccordionTitle
                    className={classNames({
                      isOpen: openAccordionId === 'lineup-bonus',
                    })}
                  >
                    <FormattedMessage
                      id="HowToPlay.levelUp.lineupBonus"
                      defaultMessage="Lineup Bonus"
                    />
                  </AccordionTitle>
                </Horizontal>
              }
            >
              <Vertical gap={2} center>
                <BodyM>
                  <FormattedMessage
                    id="HowToPlay.levelUp.lineupBonusDescription"
                    defaultMessage="Enter a team of underdogs for an additional score boost."
                  />
                </BodyM>
                {!isLaptop && (
                  <ImgContainer>
                    <Img
                      src={accordionImages['lineup-bonus'].image}
                      alt=""
                      width="623"
                      height="580"
                    />
                  </ImgContainer>
                )}
              </Vertical>
            </BaseAccordion>
            <BaseAccordion
              expanded={openAccordionId === 'collection-bonus'}
              onExpandChange={() => handleAccordionChange('collection-bonus')}
              title={
                <Horizontal>
                  <IconWrapper>
                    <Collection size={2} color="var(--c-blue-400)" />
                  </IconWrapper>
                  <AccordionTitle
                    className={classNames({
                      isOpen: openAccordionId === 'collection-bonus',
                    })}
                  >
                    <FormattedMessage
                      id="HowToPlay.levelUp.collectionBonus"
                      defaultMessage="Collection Bonus"
                    />
                  </AccordionTitle>
                </Horizontal>
              }
            >
              <Vertical gap={2} center>
                <BodyM>
                  <FormattedMessage
                    id="HowToPlay.levelUp.collectionBonusDescription"
                    defaultMessage="Own multiple cards from the same team and benefit from an even greater bonus."
                  />
                </BodyM>
                {!isLaptop && (
                  <ImgContainer>
                    <Img
                      src={accordionImages['collection-bonus'].image}
                      alt=""
                      width="545"
                      height="423"
                    />
                  </ImgContainer>
                )}
              </Vertical>
            </BaseAccordion>
          </Vertical>
        </Vertical>
      </TextContainer>
      {!!isLaptop && (
        <ImgContainer
          className={classNames({
            withBackgroundField:
              openAccordionId === 'card-xp' ||
              openAccordionId === 'lineup-bonus',
          })}
        >
          <Img
            src={accordionImages[openAccordionId ?? 'card-xp']?.image}
            alt=""
            height={accordionImages[openAccordionId ?? 'card-xp']?.height}
            width={accordionImages[openAccordionId ?? 'card-xp']?.width}
          />
        </ImgContainer>
      )}
    </Root>
  );
};
